import {
  BButton,
  BCard,
  BRow,
  BCol,
  BBadge,
  BProgress,
} from "bootstrap-vue";
import { formatDate } from "@core/utils/filter";
import FileUpload from "@/components/FileUpload/index.vue";
import { ValidationProvider, ValidationObserver } from "vee-validate";

export default {
  components: {
    BButton,
    BCard,
    BRow,
    BCol,
    BBadge,
    BProgress,
    FileUpload,
    ValidationProvider,
    ValidationObserver,
  },

  data() {
    return {
      proposal: {
        project_title: "",
        client_company_name: "",
        description: "",
        status: "",
        crm_lead_link: "",
        lead_qualification_score: "",
        expected_tech_stack: "",
        project_size: "",
        proposal_type: "",
        created_at: "",
        created_by: {},
        proposal_attachments: [],
        trails: [],
      },
      newMessage: "",
      isSendingMessage: false,
      loading: false,
      error: null,

      // Owner management
      showOwnerModal: false,
      selectedOwner: null,
      ownerList: [],
      loadingOwners: false,

      // Assignee management
      showAssigneesModal: false,
      selectedAssignees: [],
      assigneeList: [],
      loadingAssignees: false,
      searchQuery: "",

      manageDesignations: [
        "project manager",
        "technical project manager",
        "associate project manager",
        "senior project manager",
        "business analyst",
        "junior business analyst",
      ],

      allowedDesignations: [
        "senior business development manager",
        "associate business development manager",
        "business development manager",
      ],    

      bdeUserList: [],
      managerUserList: [],
      showAttachmentModal: false,
      attachments: [],
      pdfFile: [],
      remove_attachments: [],
      isSaving: false,
      isValidFile: true,
      FileErrorMsg: null,
      activity_logs: [],
    };
  },

  created() {
    // Fetch proposal details when component is created
    this.fetchProposalDetails();
  },
  computed: {
    isLimitedDesignationUser() {
      return (
        this.$store.state.app.userInfo &&
        this.$store.state.app.userInfo.designation &&
        this.allowedDesignations.includes(this.$store.state.app.userInfo.designation.toLowerCase())
      );
    },

    isManageDesignationUser() {
      return (
        this.$store.state.app.userInfo &&
        this.$store.state.app.userInfo.designation &&
        this.manageDesignations.includes(this.$store.state.app.userInfo.designation.toLowerCase())
      );
    },

    canViewFullDetails() {
      return this.isSuperAdmin || this.isLimitedDesignationUser;
    },

    canManageOwnership() {
      return this.isSuperAdmin;
    },

    isSuperAdmin() {
      // Assuming you have a way to get user role from Vuex or auth store
      return this.$store.state.app.userInfo.role === "SA";
    },
    ownerOptions() {
      return this.ownerList.map((owner) => ({
        value: owner.id,
        text: owner.full_name,
      }));
    },
    filteredAssignees() {
      if (!this.searchQuery) return this.assigneeList;
      const query = this.searchQuery.toLowerCase();
      return this.assigneeList.filter(
        (user) =>
          (user.full_name && user.full_name.toLowerCase().includes(query)) ||
          (user.email && user.email.toLowerCase().includes(query))
      );
    },

    assigneeCount() {
      return this.proposal &&
        this.proposal.proposal_assignees &&
        this.proposal.proposal_assignees.length
        ? this.proposal.proposal_assignees.length
        : 0;
    },

    hasAssignees() {
      return (
        this.proposal &&
        this.proposal.proposal_assignees &&
        this.proposal.proposal_assignees.length > 0
      );
    },

    getOwnerInitials() {
      if (!this.proposal || !this.proposal.owner || !this.proposal.owner.full_name) {
        return "U";
      }
      return this.proposal.owner.full_name.charAt(0).toUpperCase();
    },

    mappedBdeList() {
      return this.bdeUserList?.map(user => ({
        value: user.id,
        label: user.full_name?.trim() || '',
      })) || [];
    },
    mappedManagerList() {
      return this.managerUserList?.map(user => ({
        value: user.id,
        label: user.full_name?.trim() || '',
      })) || [];
    },
  },

  mounted() {
    this.readProposalMessages();
  },

  watch: {
    'proposal.trails': {
      handler() {
        this.scrollToBottom();
      },
      deep: true
    }
  },
  
  methods: {
    addActivityDiff(activityLogs) {
      if (!Array.isArray(activityLogs)) return [];

      const logs = [...activityLogs].sort(
        (a, b) => new Date(a.created_at) - new Date(b.created_at)
      );

      return logs.map((log, index) => {
        const result = {
          ...log,
          diffText: null,
          isDelayed: false,
          diffType: null,
        };

        if (index === logs.length - 1) {
          return result;
        }
        const currDate = new Date(log.created_at);
        const nextDate = new Date(logs[index + 1].created_at);
        const diffInMs = nextDate - currDate;

        const oneHour = 60 * 60 * 1000;
        const oneDay = 24 * oneHour;
        return {
          ...result,
          diffText: this.formatTimeDifference(currDate, nextDate),
          isDelayed: diffInMs >= oneDay,
          diffType: diffInMs >= oneDay ? 'long' : 'short',
        };
      });
    },

   formatTimeDifference(prevDate, currDate) {
      if (!prevDate || !currDate) return '';

      const start = new Date(prevDate);
      start.setSeconds(0, 0);

      const end = new Date(currDate);
      end.setSeconds(0, 0);

      const diffInMs = end - start;
      if (diffInMs <= 0) return '';

      const oneMinute = 60 * 1000;
      const oneHour = 60 * oneMinute;
      const oneDay = 24 * oneHour;

      const days = Math.floor(diffInMs / oneDay);
      const hours = Math.floor((diffInMs % oneDay) / oneHour);
      const minutes = Math.floor((diffInMs % oneHour) / oneMinute);

      let diffString = '';

      if (days > 0) {
        diffString += `${days} day${days > 1 ? 's' : ''} `;
      }

      if (hours > 0) {
        diffString += `${hours} hour${hours > 1 ? 's' : ''} `;
      }

      if (minutes > 0) {
        diffString += `${minutes} minute${minutes > 1 ? 's' : ''}`;
      }

      return diffString.trim();
    },
    readProposalMessages() {
      const proposalId = this.$route.params.id;
      this.getHTTPPostResponse(
        `/proposal/read-message`,
        {proposal_id: proposalId},
        false
      );
    },

    handleFileUpload(event) {
      this.pdfFile = event.file || [];
      this.isValidFile = event.isValidFile;

      if (!this.isValidFile) {
        this.FileErrorMsg = "Invalid file format or size.";
      } else {
        this.FileErrorMsg = null;
      }
    },

    removeAttachment(id) {
      this.remove_attachments.push(id);
    },

    onSubmitForReview() {
      // Only IP → IR
      if (this.proposal.status !== "IP") return;

      this.$bvModal.show("add-proposal-attachment-modal");
    },
    async userList() {
      const response = await this.getHTTPPostResponse(
        'proposal/proposal-user-list',
        {},
        false,
      )
      if (response && response.data) {
        console.log(response.data)
        const { data } = response
        this.bdeUserList = data.bde_users
        this.managerUserList = data.manager_users
      }
    },

    getInitials(user) {
      if (!user || !user.full_name) return "U";
      return user.full_name.charAt(0).toUpperCase();
    },
    getUserById(userId) {
      return (
        this.assigneeList.find((user) => user.id === userId) || {
          full_name: "Unknown User",
        }
      );
    },
    async fetchUsers() {
      try {
        await this.userList();

        this.ownerList = this.bdeUserList;
        this.assigneeList = this.managerUserList;

        if (this.proposal.owner) {
          this.selectedOwner = this.proposal.owner.id;
          }
          if (
            this.proposal.proposal_assignees &&
            this.proposal.proposal_assignees.length > 0
          ) {
            this.selectedAssignees = this.proposal.proposal_assignees
              .filter((a) => a && a.user) // Add null/undefined check for a
              .map((a) => a.user.id);
          }
      } catch (error) {
        console.error("Error fetching users:", error);
        this.$toast.error("Failed to load user list");
      }
    },
    openOwnerModal() {
      this.selectedOwner =
        this.proposal && this.proposal.owner ? this.proposal.owner.id : null;

      this.showOwnerModal = true;

      if (!this.ownerList.length) {
        this.fetchUsers();
      }
    },
    openAssigneesModal() {
      const currentAssignees = this.proposal?.proposal_assignees?.length 
        ? this.proposal.proposal_assignees.map((assignee) => assignee.user_id) 
        : [];
        
      this.selectedAssignees = [...currentAssignees];
      this.searchQuery = "";
      this.showAssigneesModal = true;

      this.fetchUsers().then(() => {
        this.selectedAssignees = [...currentAssignees];
      });
    },
    removeAssignee(userId) {
      this.selectedAssignees = this.selectedAssignees.filter(
        (id) => id !== userId
      );
    },

    async deleteAssignee(assignee) {
        const response = await this.getHTTPDeleteResponse(
          `/proposal/assignee/${assignee.id}`,
          {},
          true,
        )

        if (response && response.status === 200) {
          this.fetchProposalDetails(); // Refresh the proposal details
        }
    },
    async updateOwner() {
      if (!this.selectedOwner) return;

      this.loadingOwners = true;
        const response = await this.getHTTPPostResponse(
          `/proposal/owner/${this.proposal.id}`,
          { owner_id: this.selectedOwner },
          true
        );

        if (response && response.status === 200) {
          this.showOwnerModal = false;
          this.fetchProposalDetails();
        }
      this.loadingOwners = false;
    },

    async updateAssignees() {
      this.loadingAssignees = true;
        const response = await this.getHTTPPostResponse(
          `/proposal/assignee`,
          {
            id: this.proposal.id,
            assignees: this.selectedAssignees.map((userId) => ({
              user_id: userId,
            })),
          },
          true
        );

        if (response && response.status === 200) {
          this.showAssigneesModal = false;
          this.fetchProposalDetails();
        }
      this.loadingAssignees = false;
    },

    async fetchProposalDetails() {
      this.loading = true;
      this.error = null;
      const proposalId = this.$route.params.id;

      const response = await this.getHTTPGetResponse(
        `/proposal/view/${proposalId}`
      );
      if (response && response.status == 200) {
        this.proposal = response.data.proposal;
        this.activity_logs = this.addActivityDiff(response.data.activity);
        }
      this.loading = false;
    },
    formatDate,
    
    // Format date and time for display
    formatDateTime(dateTime) {
      if (!dateTime) return '';
      const date = new Date(dateTime);
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    },
    
    // Get current user from store
    currentUser() {
      return this.$store.state.app.userInfo || {};
    },
    
    formatMessage(text) {
  if (!text) return "";

  // Escape HTML first (prevent XSS)
  const escapedText = text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

  // Convert URLs into clickable links
  const urlRegex = /(https?:\/\/[^\s]+)/g;

  return escapedText.replace(urlRegex, (url) => {
    return `
      <a
        href="${url}"
        target="_blank"
        rel="noopener noreferrer"
        class="message-link"
      >
        ${url}
      </a>
    `;
  });
    },

    async sendMessage() {
      if (!this.newMessage.trim() || this.isSendingMessage) return;

      this.isSendingMessage = true;
      const messageText = this.newMessage.trim();

      try {
        await this.$http.post(`proposal/send-message`, {
          proposal_id: this.$route.params.id,
          message: messageText,
        });

        // clear input immediately
        this.newMessage = '';

        // fetch latest messages
        await this.fetchProposalDetails();

        // scroll will happen automatically via watcher
      } catch (error) {
        console.error(error);
        this.$toast?.error(
          error.response?.data?.message || 'Failed to send message'
        );
      } finally {
        this.isSendingMessage = false;
      }
    },
    
    // Scroll to bottom of messages when component is mounted or updated
    scrollToBottom() {
      this.$nextTick(() => {
        const container = this.$refs.messagesContainer;
        if (container) {
          container.scrollTop = container.scrollHeight;
        }
      });
    },

    getStatusVariant(status) {
      const statusMap = {
        O: "light-warning", // Open
        A: "light-success", // Assigned
        IP: "light-info", // In Progress
        IR: "light-danger", // In Review
        C: "light-secondary", // Completed
      };
      return statusMap[status] || "light-secondary";
    },
    getStatusText(status) {
      const statusText = {
        O: "Open",
        A: "Assigned",
        IP: "In Progress",
        IR: "In Review",
        C: "Completed",
      };
      return statusText[status] || "Unknown";
    },

    getFileType(filename) {
      if (!filename) return "Unknown";
      const extension = filename.split(".").pop().toLowerCase();
      const typeMap = {
        // Documents
        pdf: "PDF Document",
        doc: "Word Document",
        docx: "Word Document",
        xls: "Excel Spreadsheet",
        xlsx: "Excel Spreadsheet",
        ppt: "PowerPoint",
        pptx: "PowerPoint",
        txt: "Text File",
        rtf: "Rich Text",
        csv: "CSV File",
        // Images
        jpg: "JPEG Image",
        jpeg: "JPEG Image",
        png: "PNG Image",
        gif: "GIF Image",
        bmp: "Bitmap Image",
        svg: "Vector Image",
        // Archives
        zip: "ZIP Archive",
        rar: "RAR Archive",
        "7z": "7-Zip Archive",
        tar: "TAR Archive",
        gz: "GZIP Archive",
      };
      return typeMap[extension] || `${extension.toUpperCase()} File`;
    },
    getFileIcon(fileType) {
      if (!fileType) return "FileIcon";

      const extension = fileType.toLowerCase();
      const iconMap = {
        pdf: "FileTextIcon",
        doc: "FileTextIcon",
        docx: "FileTextIcon",
        xls: "FileTextIcon",
        xlsx: "FileTextIcon",
        jpg: "ImageIcon",
        jpeg: "ImageIcon",
        png: "ImageIcon",
        gif: "ImageIcon",
        txt: "FileTextIcon",
        zip: "ArchiveIcon",
        rar: "ArchiveIcon",
        "7z": "ArchiveIcon",
        mp3: "MusicIcon",
        mp4: "VideoIcon",
        mov: "VideoIcon",
        avi: "VideoIcon",
        default: "FileIcon",
      };

      // Extract file extension if full type is provided (e.g., 'application/pdf' -> 'pdf')
      const typeMatch = fileType.match(/\/(\w+)$/);
      const fileExtension = typeMatch ? typeMatch[1] : extension;

      return iconMap[fileExtension] || iconMap.default;
    },
    async downloadFile(attachment) {
      try {
        let URL = `${process.env.VUE_APP_API_URL}/${process.env.VUE_APP_API_VERSION}/proposal/download?url=${attachment.url}`;

        const response = await this.$axios({
          url: URL,
          method: "GET",
          params: { url: attachment.url },
          responseType: "blob",
        });

        const disposition = response.headers["content-disposition"];
        let fileName = attachment.name;
        if (disposition && disposition.indexOf("filename=") !== -1) {
          fileName = disposition.split("filename=")[1].replace(/"/g, "");
        }

        const blob = new Blob([response.data], {
          type: response.headers["content-type"],
        });
        const downloadUrl = window.URL.createObjectURL(blob);
        this.createDownloadLink(downloadUrl, fileName);
      } catch (error) {
        console.error("Error downloading file:", error);
      }
    },

    createDownloadLink(url, name) {
      const a = document.createElement("a");
      a.setAttribute("href", url);
      a.setAttribute("download", name);
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    },

    // Update status with role-based permissions
    async updateStatus(newStatus) {
      // Status transition validation
      const validTransitions = {
        O: ["A"], // Open can only go to Assigned
        A: ["IP"], // Assigned can only go to In Progress
        IP: ["IR"], // In Progress can only go to In Review
        IR: ["C", "IP"], // In Review can only go to Completed
        C: [], // Completed is final state
      };

      const currentStatus = this.proposal.status;

      // Check if transition is valid
      if (!validTransitions[currentStatus].includes(newStatus)) {
        this.$toast.error("Invalid status transition");
        return;
      }

      // Check permissions for specific status changes
      if (newStatus === "C" && !this.isSuperAdmin) {
        this.$toast.error(
          "Only Super Admin can mark the proposal as Completed"
        );
        return;
      }

      if (newStatus === "A" && !this.isSuperAdmin) {
        this.$toast.error("Only Super Admin can assign the proposal");
        return;
      }

      const response = await this.getHTTPPutResponse(
        `/proposal/update-status/${this.$route.params.id}`,
        { status: newStatus },
        true
      );

      if (response.status === 200) {
        this.fetchProposalDetails();
      }
    },


    back() {
      const { query } = this.$route;
      this.$router.push({
        path: `/${query.main_page}`,
        query,
      });
    },
    resetAttachmentModal() {
      this.attachments = [];
      this.pdfFile = [];
      this.remove_attachments = [];
      this.isValidFile = true;
      this.FileErrorMsg = null;
      this.isSaving = false;

      if (this.$refs.proposalObserver) {
        this.$refs.proposalObserver.reset();
      }
    },

    /** remove attachments */
    removeAttachments(index, id) {
      this.remove_attachments.push(id);
      this.task_attachments.splice(index, 1);
      this.attachments.splice(index, 1);
    },
  },
};
